import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Text,
  Button,
  Select,
  useToast,
  useColorModeValue,
  Spinner,
  Badge,
  Progress,
  Flex,
  Icon,
  VStack,
  HStack,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import {
  FiUsers,
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiPlus,
  FiChevronUp,
  FiChevronDown,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { dashboardService } from "../atsService";
import { jobRequisitionService } from "../jobRequisitionService";
import { FiSearch, FiFilter, FiDownload } from "react-icons/fi";
import { atsUtils } from "../atsUtils";
import CreateApplicationModal from "./CreateApplicationModal";

const ATSDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requisitions, setRequisitions] = useState([]);
  const [filteredRequisitions, setFilteredRequisitions] = useState([]);
  const [selectedRequisition, setSelectedRequisition] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedRequisitionForCreate, setSelectedRequisitionForCreate] =
    useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    department: "all",
    location: "all",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "postedDate",
    direction: "desc",
  });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [kpiError, setKpiError] = useState("");
  const toast = useToast();
  const navigate = useNavigate();

  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const hoverBg = useColorModeValue("gray.50", "gray.750");

  useEffect(() => {
    fetchRequisitions();
    fetchDashboardStats();
  }, [selectedRequisition]);

  const fetchRequisitions = async () => {
    try {
      // Get requisitions from actual API
      const { requisitions } = await jobRequisitionService.getAllRequisitions();
      const data = requisitions || [];
      setRequisitions(data);
      applyFilters(searchTerm, filters, data);
    } catch (error) {
      console.error("Error fetching requisitions:", error);
      toast({
        title: "Error",
        description: "Failed to fetch job requisitions",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setKpiError("");
      const params = selectedRequisition
        ? { requisitionId: selectedRequisition }
        : {};
      const response = await dashboardService.getDashboardStats(params);
      const kpiData = response?.data?.data ?? null;
      console.log("ATS KPI data:", kpiData);
      setStats(kpiData);
      if (!kpiData) {
        setKpiError("KPI payload is empty from server");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load KPI data";
      console.error("Error fetching dashboard stats:", message);
      setStats(null);
      setKpiError(message);
      toast({
        title: "KPI Load Failed",
        description: message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Button handlers
  const handleCreateApplication = (requisitionId = null) => {
    setSelectedRequisitionForCreate(requisitionId);
    setIsCreateModalOpen(true);
  };

  const handleReviewApplications = () => {
    if (selectedRequisition) {
      navigate(`/recruiting/applications/${selectedRequisition}`);
    } else {
      toast({
        title: "Select Requisition",
        description: "Please select a requisition to review applications",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleScheduleInterviews = () => {
    toast({
      title: "Schedule Interviews",
      description: "Interview scheduling feature coming soon!",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleGenerateReports = () => {
    toast({
      title: "Generate Reports",
      description: "Report generation feature coming soon!",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    applyFilters(term, filters);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    applyFilters(searchTerm, newFilters);
  };

  const applyFilters = (search, filterValues, data = requisitions) => {
    let filtered = [...data];

    // Apply search filter
    if (search) {
      filtered = filtered.filter(
        (req) =>
          req.jobTitle?.toLowerCase().includes(search.toLowerCase()) ||
          req.department?.toLowerCase().includes(search.toLowerCase()) ||
          req.location?.toLowerCase().includes(search.toLowerCase()) ||
          req._id?.toLowerCase().includes(search.toLowerCase()),
      );
    }

    // Apply status filter
    if (filterValues.status !== "all") {
      filtered = filtered.filter((req) => req.status === filterValues.status);
    }

    // Apply department filter
    if (filterValues.department !== "all") {
      filtered = filtered.filter(
        (req) => req.department === filterValues.department,
      );
    }

    // Apply location filter
    if (filterValues.location !== "all") {
      filtered = filtered.filter(
        (req) => req.location === filterValues.location,
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    setFilteredRequisitions(filtered);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    applyFilters(searchTerm, filters);
  };

  const getUniqueValues = (field) => {
    const values = requisitions.map((req) => req[field]).filter(Boolean);
    return [...new Set(values)].sort();
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      department: "all",
      location: "all",
    });
    setSearchTerm("");
    applyFilters("", {
      status: "all",
      department: "all",
      location: "all",
    });
  };

  const FilterModal = () => (
    <Modal
      isOpen={isFilterModalOpen}
      onClose={() => setIsFilterModalOpen(false)}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Filter Requisitions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="on_hold">On Hold</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Department</FormLabel>
              <Select
                value={filters.department}
                onChange={(e) =>
                  handleFilterChange("department", e.target.value)
                }
              >
                <option value="all">All Departments</option>
                {getUniqueValues("department").map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Location</FormLabel>
              <Select
                value={filters.location}
                onChange={(e) => handleFilterChange("location", e.target.value)}
              >
                <option value="all">All Locations</option>
                {getUniqueValues("location").map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={clearFilters}>
            Clear All
          </Button>
          <Button
            colorScheme="blue"
            onClick={() => setIsFilterModalOpen(false)}
          >
            Apply Filters
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  if (loading) {
    return (
      <Flex h="60vh" align="center" justify="center" direction="column" gap={3}>
        <Spinner size="lg" color="blue.500" thickness="3px" />
        <Text fontSize="sm" color={subtleText}>
          Loading dashboard…
        </Text>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg={bg} px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
      <VStack spacing={5} align="stretch">
        {/* Header */}
        <Flex
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align={{ base: "flex-start", sm: "center" }}
          gap={3}
        >
          <Box>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="700"
              letterSpacing="-0.02em"
            >
              ATS Dashboard
            </Text>
            <Text fontSize="sm" color={subtleText} mt={0.5}>
              Comprehensive view of your recruitment pipeline
            </Text>
          </Box>
          <HStack spacing={2} flexShrink={0}>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              size="sm"
              onClick={handleCreateApplication}
            >
              New Application
            </Button>
            <Button variant="outline" size="sm" onClick={fetchDashboardStats}>
              Refresh
            </Button>
          </HStack>
        </Flex>

        {!!kpiError && (
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={border}
            borderRadius="xl"
            px={4}
            py={3}
          >
            <Text fontSize="sm" color="red.500" fontWeight="600">
              KPI data unavailable
            </Text>
            <Text fontSize="xs" color={subtleText} mt={1}>
              {kpiError}
            </Text>
          </Box>
        )}

        {!kpiError && stats && Number(stats.totalApplications || 0) === 0 && (
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={border}
            borderRadius="xl"
            px={4}
            py={3}
          >
            <Text fontSize="sm" color={subtleText}>
              KPI data loaded from server. Current totals are zero because there
              are no ATS applications yet.
            </Text>
          </Box>
        )}

        {/* Stats 2×2 on mobile, 4-col on desktop */}
        <Grid
          templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }}
          gap={3}
        >
          {[
            {
              label: "Total Applications",
              value: stats?.totalApplications || 0,
              color: "blue.500",
            },
            {
              label: "Conversion Rate",
              value: `${(stats?.conversionRate || 0).toFixed(1)}%`,
              color: "green.500",
            },
            {
              label: "Pending Interviews",
              value: stats?.pendingInterviews || 0,
              color: "orange.500",
            },
            {
              label: "Hired",
              value: stats?.hiredCount || 0,
              color: "green.600",
            },
          ].map(({ label, value, color }) => (
            <Box
              key={label}
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              borderRadius="xl"
              px={4}
              py={3}
            >
              <Text
                fontSize="xs"
                color={subtleText}
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="600"
              >
                {label}
              </Text>
              <Text fontSize="2xl" fontWeight="700" color={color} mt={1}>
                {value}
              </Text>
            </Box>
          ))}
        </Grid>

        {/* Requisitions */}
        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={border}
          borderRadius="xl"
          overflow="hidden"
        >
          {/* Section header */}
          <Box px={4} py={3} borderBottom="1px solid" borderColor={border}>
            <Flex
              direction={{ base: "column", sm: "row" }}
              justify="space-between"
              align={{ base: "flex-start", sm: "center" }}
              gap={3}
            >
              <Text fontWeight="600" fontSize="md">
                Job Requisitions
              </Text>
              <HStack spacing={2}>
                <InputGroup size="sm" maxW={{ base: "full", sm: "220px" }}>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search…"
                    value={searchTerm}
                    onChange={handleSearch}
                    borderRadius="md"
                  />
                </InputGroup>
                <IconButton
                  icon={<FiFilter />}
                  size="sm"
                  variant="ghost"
                  aria-label="Filter"
                  onClick={() => setIsFilterModalOpen(true)}
                />
              </HStack>
            </Flex>
            {/* Active filter badges */}
            {(searchTerm ||
              filters.status !== "all" ||
              filters.department !== "all" ||
              filters.location !== "all") && (
              <Flex mt={2} wrap="wrap" gap={2}>
                {searchTerm && (
                  <Badge colorScheme="blue" variant="subtle">
                    Search: "{searchTerm}"
                  </Badge>
                )}
                {filters.status !== "all" && (
                  <Badge colorScheme="green" variant="subtle">
                    Status: {atsUtils.formatApplicationStatus(filters.status)}
                  </Badge>
                )}
                {filters.department !== "all" && (
                  <Badge colorScheme="purple" variant="subtle">
                    Dept: {filters.department}
                  </Badge>
                )}
                {filters.location !== "all" && (
                  <Badge colorScheme="orange" variant="subtle">
                    Location: {filters.location}
                  </Badge>
                )}
                <Button size="xs" variant="ghost" onClick={clearFilters}>
                  Clear
                </Button>
              </Flex>
            )}
          </Box>

          {/* Desktop table */}
          <Box display={{ base: "none", md: "block" }} overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th
                    py={3}
                    fontSize="xs"
                    cursor="pointer"
                    onClick={() => handleSort("_id")}
                  >
                    <HStack spacing={1}>
                      <Text>ID</Text>
                      {sortConfig.key === "_id" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th
                    py={3}
                    fontSize="xs"
                    cursor="pointer"
                    onClick={() => handleSort("jobTitle")}
                  >
                    <HStack spacing={1}>
                      <Text>Position</Text>
                      {sortConfig.key === "jobTitle" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th
                    py={3}
                    fontSize="xs"
                    cursor="pointer"
                    onClick={() => handleSort("department")}
                  >
                    <HStack spacing={1}>
                      <Text>Department</Text>
                      {sortConfig.key === "department" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th
                    py={3}
                    fontSize="xs"
                    cursor="pointer"
                    onClick={() => handleSort("status")}
                  >
                    <HStack spacing={1}>
                      <Text>Status</Text>
                      {sortConfig.key === "status" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th py={3} fontSize="xs">
                    Applications
                  </Th>
                  <Th
                    py={3}
                    fontSize="xs"
                    cursor="pointer"
                    onClick={() => handleSort("postedDate")}
                  >
                    <HStack spacing={1}>
                      <Text>Posted</Text>
                      {sortConfig.key === "postedDate" && (
                        <Icon
                          as={
                            sortConfig.direction === "asc"
                              ? FiChevronUp
                              : FiChevronDown
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                  <Th py={3} />
                </Tr>
              </Thead>
              <Tbody>
                {filteredRequisitions.map((req) => (
                  <Tr
                    key={req._id}
                    cursor="pointer"
                    _hover={{ bg: hoverBg }}
                    onClick={() => navigate(`/recruiting/ats/${req._id}`)}
                  >
                    <Td py={3}>
                      <Text fontSize="xs" color={subtleText}>
                        {req._id?.slice(-8) || "N/A"}
                      </Text>
                    </Td>
                    <Td py={3}>
                      <Text fontWeight="500" fontSize="sm">
                        {req.jobTitle || "N/A"}
                      </Text>
                      <Text fontSize="xs" color={subtleText}>
                        {req.location || "N/A"}
                      </Text>
                    </Td>
                    <Td py={3}>
                      <Badge variant="subtle" colorScheme="blue" fontSize="xs">
                        {req.department || "N/A"}
                      </Badge>
                    </Td>
                    <Td py={3}>
                      <Badge
                        colorScheme={atsUtils.getStatusColor(req.status)}
                        fontSize="xs"
                      >
                        {atsUtils.formatApplicationStatus(req.status) || "N/A"}
                      </Badge>
                    </Td>
                    <Td py={3}>
                      <Text fontWeight="500" fontSize="sm">
                        {req.applicationsCount || 0}
                      </Text>
                    </Td>
                    <Td py={3}>
                      <Text fontSize="xs">
                        {req.postedDate
                          ? new Date(req.postedDate).toLocaleDateString()
                          : "N/A"}
                      </Text>
                    </Td>
                    <Td py={3} onClick={(e) => e.stopPropagation()}>
                      <HStack spacing={0}>
                        <IconButton
                          aria-label="View"
                          icon={<FiEye />}
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/recruiting/ats/${req._id}`);
                          }}
                        />
                        <IconButton
                          aria-label="Add application"
                          icon={<FiPlus />}
                          size="sm"
                          variant="ghost"
                          colorScheme="green"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateApplication(req._id);
                          }}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {filteredRequisitions.length === 0 && (
              <Box textAlign="center" py={10}>
                <Text fontSize="sm" color={subtleText}>
                  {searchTerm
                    ? "No matching requisitions found"
                    : "No requisitions found"}
                </Text>
              </Box>
            )}
          </Box>

          {/* Mobile card list */}
          <VStack
            display={{ base: "flex", md: "none" }}
            spacing={0}
            align="stretch"
            divider={<Divider />}
          >
            {filteredRequisitions.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Text fontSize="sm" color={subtleText}>
                  {searchTerm
                    ? "No matching requisitions found"
                    : "No requisitions found"}
                </Text>
              </Box>
            ) : (
              filteredRequisitions.map((req) => (
                <Box
                  key={req._id}
                  px={4}
                  py={3}
                  cursor="pointer"
                  _active={{ opacity: 0.8 }}
                  onClick={() => navigate(`/recruiting/ats/${req._id}`)}
                >
                  <Flex justify="space-between" align="flex-start" mb={1.5}>
                    <Box flex={1} mr={2}>
                      <Text fontWeight="600" fontSize="sm" noOfLines={1}>
                        {req.jobTitle || "N/A"}
                      </Text>
                      <Text fontSize="xs" color={subtleText}>
                        {req.location || "N/A"} · #{req._id?.slice(-6)}
                      </Text>
                    </Box>
                    <Badge
                      colorScheme={atsUtils.getStatusColor(req.status)}
                      fontSize="xs"
                      flexShrink={0}
                    >
                      {atsUtils.formatApplicationStatus(req.status) || "N/A"}
                    </Badge>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={2}>
                      <Badge variant="subtle" colorScheme="blue" fontSize="xs">
                        {req.department || "N/A"}
                      </Badge>
                      <Text fontSize="xs" color={subtleText}>
                        {req.applicationsCount || 0} apps
                      </Text>
                    </HStack>
                    <HStack spacing={0} onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        aria-label="View"
                        icon={<FiEye />}
                        size="xs"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/recruiting/ats/${req._id}`);
                        }}
                      />
                      <IconButton
                        aria-label="Add"
                        icon={<FiPlus />}
                        size="xs"
                        variant="ghost"
                        colorScheme="green"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateApplication(req._id);
                        }}
                      />
                    </HStack>
                  </Flex>
                </Box>
              ))
            )}
          </VStack>
        </Box>

        {/* Pipeline */}
        {stats?.stageBreakdown?.length > 0 && (
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={border}
            borderRadius="xl"
            overflow="hidden"
          >
            <Box px={4} py={3} borderBottom="1px solid" borderColor={border}>
              <Text fontWeight="600" fontSize="md">
                Recruitment Pipeline
              </Text>
              <Text fontSize="sm" color={subtleText} mt={0.5}>
                Distribution of applications across stages
              </Text>
            </Box>
            <Box p={4}>
              <Grid
                templateColumns={{
                  base: "1fr",
                  sm: "1fr 1fr",
                  md: "repeat(3, 1fr)",
                }}
                gap={3}
              >
                {stats.stageBreakdown.map((stage) => {
                  const pct =
                    stats.totalApplications > 0
                      ? ((stage.count / stats.totalApplications) * 100).toFixed(
                          1,
                        )
                      : 0;
                  return (
                    <Box
                      key={stage._id}
                      p={3}
                      border="1px solid"
                      borderColor={border}
                      borderRadius="lg"
                    >
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text fontWeight="500" fontSize="sm">
                          {atsUtils.formatApplicationStatus(stage._id)}
                        </Text>
                        <Badge
                          colorScheme={atsUtils.getStatusColor(stage._id)}
                          fontSize="xs"
                        >
                          {stage.count}
                        </Badge>
                      </Flex>
                      <Progress
                        value={pct}
                        colorScheme={atsUtils.getStatusColor(stage._id)}
                        size="sm"
                        borderRadius="full"
                      />
                      <Text fontSize="xs" color={subtleText} mt={1}>
                        {pct}% of total
                      </Text>
                      {stage.avgScore && (
                        <Text fontSize="xs" color={subtleText}>
                          Avg score: {stage.avgScore.toFixed(1)}/100
                        </Text>
                      )}
                    </Box>
                  );
                })}
              </Grid>
            </Box>
          </Box>
        )}

        {/* Quick Actions + Recent Activity */}
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={border}
            borderRadius="xl"
            p={4}
          >
            <Text fontWeight="600" fontSize="md" mb={3}>
              Quick Actions
            </Text>
            <VStack spacing={2} align="stretch">
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                variant="outline"
                size="sm"
                onClick={handleCreateApplication}
              >
                Create New Application
              </Button>
              <Button
                leftIcon={<FiEye />}
                colorScheme="green"
                variant="outline"
                size="sm"
                onClick={handleReviewApplications}
              >
                Review Applications
              </Button>
              <Button
                leftIcon={<FiClock />}
                colorScheme="orange"
                variant="outline"
                size="sm"
                onClick={handleScheduleInterviews}
              >
                Schedule Interviews
              </Button>
              <Button
                leftIcon={<FiCheckCircle />}
                colorScheme="purple"
                variant="outline"
                size="sm"
                onClick={handleGenerateReports}
              >
                Generate Reports
              </Button>
            </VStack>
          </Box>
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={border}
            borderRadius="xl"
            p={4}
          >
            <Text fontWeight="600" fontSize="md" mb={3}>
              Recent Activity
            </Text>
            <Text fontSize="sm" color={subtleText}>
              No recent activity to display
            </Text>
          </Box>
        </Grid>
      </VStack>

      <CreateApplicationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        requisitionId={selectedRequisitionForCreate}
        onSuccess={() => {
          fetchRequisitions();
          fetchDashboardStats();
        }}
      />

      <FilterModal />
    </Box>
  );
};

export default ATSDashboard;
